import { useMemo, useState, useEffect, useCallback } from "react";
import DOMPurify from "dompurify";
import { Trash2, Star, Tag, Bookmark, Paperclip, Clock, Reply, Forward, Mail, MailOpen, FileText, Image as ImageIcon, Download, ExternalLink, X, UserPlus, UserCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import AssignmentDropdown from "@/components/AssignmentDropdown";
import MoveToMenu from "@/components/MoveToMenu";
import api from "@/lib/api";
import { API_BASE_URL } from "@/lib/api";
import { shareOrDownloadFile } from "@/lib/attachments";
import { toast } from "sonner";
import { format } from "date-fns";

function formatBytes(n) {
  if (!n) return "0 B";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function attachmentIcon(mime) {
  const m = (mime || "").toLowerCase();
  if (m.startsWith("image/")) return ImageIcon;
  if (m === "application/pdf") return FileText;
  return Paperclip;
}

function parseUTC(dateStr) {
  if (!dateStr) return new Date();
  let s = String(dateStr);
  s = s.replace(/[Zz]$/, "").replace(/\+00:?00$/, "");
  s = s.replace(/(\.\d{3})\d*$/, "$1");
  if (!s.endsWith("Z")) s += "Z";
  return new Date(s);
}

function sanitizeHtml(html) {
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: [
      "p", "br", "div", "span", "a", "b", "strong", "i", "em", "u",
      "h1", "h2", "h3", "h4", "h5", "h6", "ul", "ol", "li",
      "table", "thead", "tbody", "tr", "th", "td", "blockquote",
      "pre", "code", "hr", "img", "sup", "sub", "small",
    ],
    ALLOWED_ATTR: [
      "href", "target", "rel", "src", "alt", "width", "height",
      "style", "class", "align", "valign", "cellpadding", "cellspacing",
      "border", "colspan", "rowspan",
    ],
    ALLOW_DATA_ATTR: false,
    ADD_ATTR: ["target"],
  });
}

/**
 * Tiny additive control for the email detail sender row. Shows
 * "Add contact" or "Saved contact" depending on whether the sender's
 * email already exists in the user's address book. Treats backend 409
 * (duplicate) as the success case for "Saved contact".
 */
function AddContactButton({ email: senderEmail, name: senderName }) {
  const [state, setState] = useState("idle"); // idle | saving | saved
  const [busy, setBusy] = useState(false);

  const lookup = useCallback(async () => {
    if (!senderEmail) return;
    try {
      const { data } = await api.get(`/contacts/autocomplete`, {
        params: { q: senderEmail, limit: 5 },
      });
      const lower = senderEmail.toLowerCase();
      const hit = (data?.results || []).some(
        (r) => (r?.email || "").toLowerCase() === lower
      );
      setState(hit ? "saved" : "idle");
    } catch {
      // Silent — never block the email view on this background check.
    }
  }, [senderEmail]);

  useEffect(() => { lookup(); }, [lookup]);

  if (!senderEmail) return null;

  const handleAdd = async () => {
    if (busy || state === "saved") return;
    setBusy(true);
    setState("saving");
    try {
      await api.post(`/contacts`, {
        email: senderEmail,
        name: senderName || "",
      });
      setState("saved");
      toast.success("Saved to contacts");
    } catch (err) {
      const status = err?.response?.status;
      if (status === 409) {
        // Already exists — treat as success.
        setState("saved");
      } else {
        setState("idle");
        toast.error(err?.response?.data?.detail || "Could not save contact");
      }
    } finally {
      setBusy(false);
    }
  };

  if (state === "saved") {
    return (
      <span
        className="inline-flex items-center gap-1 text-[10px] text-emerald-600 font-medium"
        data-testid="add-contact-saved"
        title="Already in your contacts"
      >
        <UserCheck size={11} />
        Saved contact
      </span>
    );
  }

  return (
    <button
      type="button"
      onClick={handleAdd}
      disabled={busy}
      className="inline-flex items-center gap-1 text-[10px] text-[#555555] hover:text-[#111111] font-medium disabled:opacity-50"
      data-testid="add-contact-button"
      title="Save sender to contacts"
    >
      <UserPlus size={11} />
      {busy ? "Saving…" : "Add contact"}
    </button>
  );
}

export default function EmailDetail({
  email, onAction, categories, labels, onClose, isMobile, folder,
  onReply, onForward, onCreateCategory, onDeleteCategory, onCreateLabel, onDeleteLabel,
}) {
  const hasHtmlBody = !!(email?.body_html && email.body_html.trim());

  // Contained inline preview viewer for image + PDF attachments. Anything
  // else now triggers a direct download (PROMPT 023: external/new-tab
  // opens are gone — every attachment must stay inside the SPA).
  const [previewAtt, setPreviewAtt] = useState(null);

  // PROMPT 023 — PDF preview must render INSIDE the modal on iOS Safari.
  // Pointing <embed> at a raw `/api/...` URL fails on iOS (blank page or
  // "Frame wurde unterbrochen") because Safari treats Content-Disposition
  // responses as top-level navigations even from within iframes/embeds.
  // Workaround: fetch the PDF as a blob, force the MIME type to
  // `application/pdf`, and feed the resulting `blob:` URL to <embed>.
  // iOS Safari renders blob: PDFs inline without leaving the page.
  const [previewBlobUrl, setPreviewBlobUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(false);

  useEffect(() => {
    // Only PDFs need the blob dance. Images keep working with raw <img src>.
    if (!previewAtt || previewAtt._kind !== "pdf") {
      setPreviewBlobUrl(null);
      setPreviewLoading(false);
      setPreviewError(false);
      return;
    }
    let cancelled = false;
    let objectUrl = null;
    setPreviewLoading(true);
    setPreviewError(false);
    (async () => {
      try {
        const res = await fetch(previewAtt._url, { credentials: "include" });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const raw = await res.blob();
        if (cancelled) return;
        // Re-wrap with explicit application/pdf MIME — some backends
        // serve attachments as application/octet-stream which iOS won't
        // render inline.
        const pdfBlob = raw.type === "application/pdf"
          ? raw
          : new Blob([raw], { type: "application/pdf" });
        objectUrl = URL.createObjectURL(pdfBlob);
        if (cancelled) {
          URL.revokeObjectURL(objectUrl);
          return;
        }
        setPreviewBlobUrl(objectUrl);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn("[EmailDetail] PDF preview blob fetch failed:", err);
        if (!cancelled) setPreviewError(true);
      } finally {
        if (!cancelled) setPreviewLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      if (objectUrl) {
        try { URL.revokeObjectURL(objectUrl); } catch { /* noop */ }
      }
    };
  }, [previewAtt]);

  const sanitizedHtml = useMemo(() => {
    if (hasHtmlBody) return sanitizeHtml(email.body_html);
    return null;
  }, [email?.body_html, hasHtmlBody]);

  if (!email) return null;

  const handleAction = (action, id) => {
    if (action === "add_category" || action === "remove_category") {
      onAction(email.email_id, action, id, null);
    } else if (action === "add_label" || action === "remove_label") {
      onAction(email.email_id, action, null, id);
    } else {
      onAction(email.email_id, action);
    }
  };

  const formattedDate = email.created_at
    ? format(parseUTC(email.created_at), "MMM d, yyyy 'at' h:mm a")
    : "";

  const hasTextBody = !!(email.body && email.body.trim());

  return (
    <div className={`flex-1 flex flex-col ${isMobile ? "" : "bg-white border-l border-[#E5E5E5]"}`} data-testid="email-detail-view">
      {/* Action Bar — Category / Labels visible; ⋯ menu holds secondary actions.
          On mobile, Reply/Forward/Delete move to a fixed bottom bar (rendered below). */}
      <div className="flex items-start justify-between gap-2 px-4 sm:px-6 py-2.5 border-b border-[#E5E5E5] bg-white shrink-0">
        <div className="flex items-center gap-0.5 flex-wrap">
          {/* Category + Labels — visible, not hidden */}
          <AssignmentDropdown
            items={(categories || []).map(c => ({ id: c.category_id, name: c.name, color: c.color }))}
            selectedIds={email.category_ids || []}
            onToggle={(id, wasSelected) => handleAction(wasSelected ? "remove_category" : "add_category", id)}
            onCreate={onCreateCategory}
            onDelete={onDeleteCategory}
            maxItems={10}
            label="Category"
            icon={Tag}
            counterLabel="categories"
          />
          <AssignmentDropdown
            items={(labels || []).map(l => ({ id: l.label_id, name: l.name, color: l.color }))}
            selectedIds={email.label_ids || []}
            onToggle={(id, wasSelected) => handleAction(wasSelected ? "remove_label" : "add_label", id)}
            onCreate={onCreateLabel}
            onDelete={onDeleteLabel}
            maxItems={10}
            label="Labels"
            icon={Bookmark}
            counterLabel="labels"
            showColorDot={false}
          />

          <Separator orientation="vertical" className="h-5 mx-1" />

          {/* Primary actions — always visible icon buttons.
              Per-action visibility:
                - Reply / Forward only if a handler is provided AND on inbound
                  (Sent → Forward only; Reply makes no sense for outbound).
                - Read/unread hidden for outbound (no read state).
                - Star/unstar hidden for outbound (no vip state on outbound rows).
                - Archive/Delete always shown when not in archive folder. */}
          {email.direction !== "outbound" && onReply && (
            <Button
              variant="ghost"
              size="sm"
              className="text-[#555555] hover:text-[#111111] h-9 w-9 p-0"
              onClick={() => onReply(email)}
              title="Reply"
              aria-label="Reply"
              data-testid="detail-reply-button"
            >
              <Reply size={16} />
            </Button>
          )}
          {onForward && (
            <Button
              variant="ghost"
              size="sm"
              className="text-[#555555] hover:text-[#111111] h-9 w-9 p-0"
              onClick={() => onForward(email)}
              title="Forward"
              aria-label="Forward"
              data-testid="detail-forward-button"
            >
              <Forward size={16} />
            </Button>
          )}
          {email.direction !== "outbound" && (
            <Button
              variant="ghost"
              size="sm"
              className="text-[#555555] hover:text-[#111111] h-9 w-9 p-0"
              onClick={() => handleAction(email.status === "unread" ? "read" : "unread")}
              title={email.status === "unread" ? "Mark as read" : "Mark as unread"}
              aria-label={email.status === "unread" ? "Mark as read" : "Mark as unread"}
              data-testid="detail-read-state-button"
            >
              {email.status === "unread" ? <MailOpen size={16} /> : <Mail size={16} />}
            </Button>
          )}
          {email.direction !== "outbound" && (
            <Button
              variant="ghost"
              size="sm"
              className="h-9 w-9 p-0"
              onClick={() => handleAction(email.is_vip ? "unvip" : "vip")}
              title={email.is_vip ? "Remove star" : "Add star"}
              aria-label={email.is_vip ? "Remove star" : "Add star"}
              data-testid="detail-star-button"
            >
              <Star size={16} className={email.is_vip ? "text-amber-500 fill-amber-500" : "text-[#555555] hover:text-[#111111]"} />
            </Button>
          )}
          {folder !== "sent" && email.direction !== "outbound" && (
            <MoveToMenu
              folder={folder}
              onAction={(action) => handleAction(action)}
              variant="icon"
              testIdPrefix="detail-move-to"
            />
          )}
          <Button
            variant="ghost"
            size="sm"
            className="text-red-600 hover:bg-red-50 hover:text-red-700 h-9 w-9 p-0"
            onClick={() => handleAction("delete")}
            title="Delete"
            aria-label="Delete"
            data-testid="detail-delete-button"
          >
            <Trash2 size={16} />
          </Button>
        </div>
      </div>

      {/* Email Content */}
      <div className={`flex-1 overflow-auto px-4 sm:px-6 py-5 space-y-4 ${isMobile ? "pb-6" : ""}`}>
        {/* Row 1: Subject (star moved to single top action bar — PROMPT 007) */}
        <div className="flex items-start gap-3">
          <h2 className="font-heading text-xl sm:text-2xl tracking-tight font-medium text-[#111111] flex-1" data-testid="detail-subject">
            {email.subject}
          </h2>
        </div>

        {/* Row 2: Categories + Labels — all visible, truncated */}
        {((email.category_ids?.length > 0) || (email.label_ids?.length > 0)) && (
          <div className="flex items-center gap-1.5 flex-wrap" data-testid="detail-badges">
            {(email.category_ids || []).map(id => {
              const cat = (categories || []).find(c => c.category_id === id);
              if (!cat) return null;
              return (
                <span key={id} title={cat.name} className="inline-flex items-center text-[10px] font-medium text-white px-1.5 py-0.5 rounded" style={{ backgroundColor: cat.color }}>
                  {cat.name.length > 8 ? cat.name.slice(0, 8) + "…" : cat.name}
                </span>
              );
            })}
            {(email.label_ids || []).map(id => {
              const lbl = (labels || []).find(l => l.label_id === id);
              if (!lbl) return null;
              return (
                <span key={id} title={lbl.name} className="inline-flex items-center text-[10px] text-[#555555] bg-[#F5F5F5] border border-[#E5E5E5] px-1.5 py-0.5 rounded">
                  {lbl.name.length > 8 ? lbl.name.slice(0, 8) + "…" : lbl.name}
                </span>
              );
            })}
          </div>
        )}

        <Separator />

        {/* Row 3: Sender / Recipient / Date */}
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-[#F5F5F5] flex items-center justify-center text-sm font-medium text-[#555555] shrink-0">
            {email.direction === "outbound"
              ? (email.to_email?.charAt(0)?.toUpperCase() || "?")
              : (email.sender_name?.charAt(0)?.toUpperCase() || "?")
            }
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-baseline justify-between gap-2">
              {email.direction === "outbound" ? (
                <p className="text-sm font-semibold text-[#111111]">To: {email.to_email}</p>
              ) : (
                <p className="text-sm font-semibold text-[#111111]">{email.sender_name}</p>
              )}
              <div className="flex items-center gap-1 text-[11px] text-[#888888] shrink-0">
                <Clock size={11} />
                <span>{formattedDate}</span>
              </div>
            </div>
            {email.direction === "outbound" ? (
              <p className="text-xs text-[#888888]">From: {email.sender_email || email.alias_address}</p>
            ) : (
              <p className="text-xs text-[#888888]">{email.sender_email}</p>
            )}
            {email.direction !== "outbound" && email.sender_email && (
              <div className="mt-1">
                <AddContactButton
                  email={email.sender_email}
                  name={email.sender_name}
                />
              </div>
            )}
            {email.alias_address && (
              <p className="text-[10px] text-[#888888] mt-1">
                to: {email.alias_address}
              </p>
            )}
          </div>
        </div>

        {/* Row 4: Attachment list. Each item is clickable —
              • previewable (PDF/JPG/PNG) opens in a new tab via the
                Content-Disposition: inline response on the backend
              • non-previewable types fall through to a plain download
            The download icon button always forces ?download=1.
            Outbound (sent) attachments without a stored file_path show a
            disabled chip so the user understands they can't be re-fetched. */}
        {(() => {
          const atts = email.attachments || [];
          const count = atts.length || (email.has_attachments ? 1 : 0);
          if (!count) {
            return (
              <p className="text-xs text-[#888888]" data-testid="detail-attachment-count">
                0 attachments
              </p>
            );
          }
          return (
            <div className="space-y-1.5" data-testid="detail-attachments">
              <p
                className="text-xs text-[#888888] flex items-center gap-1.5"
                data-testid="detail-attachment-count"
              >
                <Paperclip size={11} />
                {count} attachment{count !== 1 ? "s" : ""}
              </p>
              <ul className="space-y-1.5">
                {atts.map((att, idx) => {
                  const Icon = attachmentIcon(att.mime_type);
                  const url = att.download_url
                    ? `${API_BASE_URL}${att.download_url}`
                    : null;
                  const dlUrl = url ? `${url}?download=1` : null;
                  const usable = !!url;
                  return (
                    <li
                      key={att.attachment_id || idx}
                      className={`flex items-center gap-2 px-2.5 py-1.5 border border-[#E5E5E5] rounded-md bg-[#FAFAFA] ${
                        usable ? "hover:bg-[#F0F0F0] cursor-pointer" : "opacity-60 cursor-not-allowed"
                      }`}
                      data-testid={`detail-attachment-item-${idx}`}
                      onClick={() => {
                        if (!usable) return;
                        // PROMPT 023: Image + PDF → open contained in-page
                        // viewer. Any other type → trigger an in-app blob
                        // download (no more external/new-tab opens — every
                        // attachment must stay inside the SPA on iOS).
                        const mime = (att.mime_type || "").toLowerCase();
                        const isImg = mime.startsWith("image/");
                        const isPdf = mime === "application/pdf";
                        if (att.previewable && (isImg || isPdf)) {
                          setPreviewAtt({ ...att, _url: url, _dlUrl: dlUrl, _kind: isPdf ? "pdf" : "image" });
                        } else {
                          shareOrDownloadFile(dlUrl, att.filename);
                        }
                      }}
                      onKeyDown={(e) => {
                        if (!usable) return;
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          const mime = (att.mime_type || "").toLowerCase();
                          const isImg = mime.startsWith("image/");
                          const isPdf = mime === "application/pdf";
                          if (att.previewable && (isImg || isPdf)) {
                            setPreviewAtt({ ...att, _url: url, _dlUrl: dlUrl, _kind: isPdf ? "pdf" : "image" });
                          } else {
                            shareOrDownloadFile(dlUrl, att.filename);
                          }
                        }
                      }}
                      role={usable ? "button" : undefined}
                      tabIndex={usable ? 0 : -1}
                      title={usable ? (att.previewable ? "Open preview" : "Download") : "File no longer available"}
                    >
                      <Icon size={14} className="text-zinc-500 shrink-0" />
                      <span
                        className="text-xs text-[#333333] truncate flex-1"
                        data-testid={`detail-attachment-name-${idx}`}
                      >
                        {att.filename || "attachment"}
                      </span>
                      <span className="text-[10px] text-zinc-500 font-mono shrink-0">
                        {formatBytes(att.file_size)}
                      </span>
                      {usable && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            // Native <a download> is silently ignored on
                            // iOS Safari for cross-document URLs — the
                            // browser navigates the current tab instead,
                            // dumping the user out of the SPA. We fetch
                            // + blob + click a hidden anchor whose href
                            // is a `blob:` URL — iOS Safari DOES honor
                            // `download` on blob URLs.
                            shareOrDownloadFile(dlUrl, att.filename);
                          }}
                          className="text-zinc-500 hover:text-[#111111] shrink-0 p-1 rounded hover:bg-white"
                          aria-label={`Download ${att.filename || "file"}`}
                          data-testid={`detail-attachment-download-${idx}`}
                          title="Download"
                        >
                          <Download size={13} />
                        </button>
                      )}
                      {att.previewable && usable && (
                        <ExternalLink size={11} className="text-zinc-400 shrink-0" />
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          );
        })()}

        <Separator />

        {/* Body — HTML → Text → Snippet fallback */}
        {hasHtmlBody ? (
          <div
            className="email-html-body text-sm text-[#555555] leading-relaxed"
            data-testid="detail-body-html"
            dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
          />
        ) : hasTextBody ? (
          <div className="text-sm text-[#555555] leading-relaxed whitespace-pre-wrap" data-testid="detail-body-text">
            {email.body}
          </div>
        ) : (
          <div className="text-sm text-[#888888] italic" data-testid="detail-body-empty">
            {email.body_preview || "No content available."}
          </div>
        )}
      </div>

      {/* Mobile fixed bottom bar removed — single top action bar is the only
          surface for primary actions per PROMPT 007. */}

      {/* Contained attachment preview — Shadcn Dialog gives us backdrop
          click, ESC-to-close, focus trap and scroll lock for free. The
          custom max-w/max-h keeps it "slightly smaller than the viewport"
          per spec, and the close button + download button are always
          reachable above the scroll/preview area. */}
      <Dialog open={!!previewAtt} onOpenChange={(o) => !o && setPreviewAtt(null)}>
        <DialogContent
          className="p-0 gap-0 bg-white overflow-hidden sm:max-w-[900px]"
          style={{
            width: "calc(100vw - 24px)",
            // `dvh` (dynamic viewport height) honors iOS Safari's
            // collapsing toolbar; falls back to `vh` on older browsers
            // that don't recognize the unit (the second declaration
            // simply overrides the first when supported).
            height: "calc(100vh - 120px)",
            maxWidth: "900px",
            maxHeight: "min(80vh, 80dvh)",
          }}
          data-testid="attachment-preview-dialog"
        >
          <DialogTitle className="sr-only">
            {previewAtt?.filename || "Attachment preview"}
          </DialogTitle>
          {previewAtt && (
            <div className="flex flex-col h-full">
              {/* Viewer header: filename · size · Download · X (close) */}
              <div className="flex items-center gap-2 px-3 py-2 border-b border-[#E5E5E5] bg-[#FAFAFA] shrink-0">
                <Paperclip size={13} className="text-zinc-500 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-medium text-[#111111] truncate" data-testid="attachment-preview-name">
                    {previewAtt.filename || "attachment"}
                  </div>
                  <div className="text-[10px] text-zinc-500 font-mono">
                    {formatBytes(previewAtt.file_size)}
                  </div>
                </div>
                {/* iOS-safe download: a raw <a href download> would
                    navigate iOS Safari to the API URL instead of saving
                    the file (the browser ignores `download` for cross-
                    document anchors and treats Content-Disposition as a
                    full-page navigation, dumping the user out of the
                    SPA into a "Frame wurde unterbrochen" page). Routed
                    through shareOrDownloadFile → on iOS the system
                    Share Sheet ("Save to Files" / AirDrop / Mail)
                    appears; on desktop it falls back to a classic blob
                    anchor download. SPA tab is never navigated. */}
                <button
                  type="button"
                  onClick={() => shareOrDownloadFile(previewAtt._dlUrl, previewAtt.filename)}
                  className="text-zinc-500 hover:text-[#111111] p-1.5 rounded hover:bg-white"
                  aria-label="Download"
                  data-testid="attachment-preview-download"
                  title="Download"
                >
                  <Download size={14} />
                </button>
                <button
                  type="button"
                  onClick={() => setPreviewAtt(null)}
                  className="text-zinc-500 hover:text-[#111111] p-1.5 rounded hover:bg-white"
                  aria-label="Close preview"
                  data-testid="attachment-preview-close"
                  title="Close"
                >
                  <X size={16} />
                </button>
              </div>

              {/* Viewer body */}
              <div className="flex-1 min-h-0 bg-[#F5F5F5] overflow-auto">
                {previewAtt._kind === "image" ? (
                  <img
                    src={previewAtt._url}
                    alt={previewAtt.filename || ""}
                    className="mx-auto my-auto max-w-full max-h-full object-contain block"
                    style={{ display: "block", margin: "auto" }}
                    data-testid="attachment-preview-image"
                  />
                ) : (
                  // PROMPT 023 — PDF rendered from a `blob:` URL so it
                  // displays inline on iOS Safari without ever leaving
                  // the SPA. The blob is fetched in the useEffect above
                  // (forces application/pdf MIME). While the fetch is
                  // in flight we show a quiet loading state; on failure
                  // we expose a "Download instead" CTA — never a
                  // new-tab/external escape hatch.
                  <div className="flex flex-col h-full" data-testid="attachment-preview-pdf-wrap">
                    {previewLoading && (
                      <div
                        className="flex-1 w-full flex items-center justify-center text-xs text-zinc-500 bg-white"
                        data-testid="attachment-preview-pdf-loading"
                      >
                        Loading preview…
                      </div>
                    )}
                    {!previewLoading && previewError && (
                      <div
                        className="flex-1 w-full flex flex-col items-center justify-center gap-3 text-xs text-zinc-600 bg-white px-6 text-center"
                        data-testid="attachment-preview-pdf-error"
                      >
                        <span>Couldn’t render this PDF inline.</span>
                        <button
                          type="button"
                          onClick={() => shareOrDownloadFile(previewAtt._dlUrl, previewAtt.filename)}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-[#E5E5E5] bg-white hover:bg-[#F5F5F5] text-[#111111] cursor-pointer"
                          data-testid="attachment-preview-pdf-error-download"
                        >
                          <Download size={12} /> Download instead
                        </button>
                      </div>
                    )}
                    {!previewLoading && !previewError && previewBlobUrl && (
                      <embed
                        src={previewBlobUrl}
                        type="application/pdf"
                        className="flex-1 w-full border-0 bg-white"
                        data-testid="attachment-preview-pdf"
                      />
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
